'use strict'

// Map a CSV onto a spreadsheet that already exists: which file column lands
// on which field, which value matches an existing row, and which relationship
// label matches a related record. The failure report is the CSV a builder downloads.

const { csvLine } = require('./spreadsheet-csv')
const { storeDateTime, storeDateOnly, isDateOnlyColumn } = require('./app-timezone')
const { relationshipChoice } = require('./relationship-column')

const IMPORTABLE_TYPES = new Set([
  'text',
  'number',
  'dateTime',
  'multiselect',
  'relationship',
  'richText',
  'address',
  'duration'
])

const KEY_TYPES = new Set(['text', 'number', 'dateTime'])

function importableColumn (column) {

  return !!(column && column.uniqueTitle && IMPORTABLE_TYPES.has(column.type))
}

function importableColumns (columns) {

  return (columns || []).filter(importableColumn)
}

function keyColumnAllowed (column) {

  return !!(column && column.uniqueTitle && KEY_TYPES.has(column.type))
}

function parseCsv (text) {

  const source = String(text || '').replace(/^\uFEFF/, '')
  const table = []
  let row = []
  let cell = ''
  let quoted = false

  for (let index = 0; index < source.length; index += 1) {

    const character = source[index]

    if (quoted) {

      if (character === '"') {

        if (source[index + 1] === '"') {

          cell += '"'
          index += 1
        } else {

          quoted = false
        }
      } else {

        cell += character
      }

      continue
    }

    if (character === '"') {

      quoted = true
    } else if (character === ',') {

      row.push(cell)
      cell = ''
    } else if (character === '\n') {

      row.push(cell)
      table.push(row)
      row = []
      cell = ''
    } else if (character !== '\r') {

      cell += character
    }
  }

  if (cell.length || row.length) {

    row.push(cell)
    table.push(row)
  }

  const filled = table.filter((line) => line.some((value) => String(value).trim() !== ''))

  if (!filled.length) return { headers: [], rows: [] }

  return {
    headers: filled[0].map((value) => String(value).trim()),
    rows: filled.slice(1).map((line) => line.map((value) => String(value).trim()))
  }
}

function columnMatch (header, columns, used) {

  const needle = String(header || '').trim().toLowerCase()

  if (!needle) return ''

  const available = importableColumns(columns).filter((column) => !used.has(column.uniqueTitle))
  const byId = available.find((column) => String(column.uniqueTitle).toLowerCase() === needle)

  if (byId) return byId.uniqueTitle

  const byTitle = available.find((column) => String(column.title || '').trim().toLowerCase() === needle)

  return byTitle ? byTitle.uniqueTitle : ''
}

function suggestColumnMap (headers, columns) {

  const used = new Set()

  return (headers || []).map((header) => {

    const uniqueTitle = columnMatch(header, columns, used)

    if (uniqueTitle) used.add(uniqueTitle)

    return uniqueTitle
  })
}

function normalizeColumnMap (headers, mapping) {

  if (!Array.isArray(headers) || !headers.length) {

    throw new Error('The file needs a header row.')
  }

  if (!Array.isArray(mapping) || mapping.length !== headers.length) {

    throw new Error('Map each file column.')
  }

  const used = new Set()
  const pairs = []

  mapping.forEach((column, index) => {

    const uniqueTitle = String(column || '').trim()

    if (!uniqueTitle) return

    if (used.has(uniqueTitle)) throw new Error('Each spreadsheet column can be mapped once.')

    used.add(uniqueTitle)
    pairs.push({ index, uniqueTitle })
  })

  if (!pairs.length) throw new Error('Map at least one file column.')

  return pairs
}

function keyIdentity (value, column, timeZone) {

  if (value == null || value === '') return ''

  if (!column) return String(value).trim()

  if (column.type === 'number') {

    const number = Number(value)

    return Number.isFinite(number) ? String(number) : ''
  }

  if (column.type === 'dateTime') {

    const stored = isDateOnlyColumn(column) ? storeDateOnly(value) : storeDateTime(value, timeZone)

    return stored == null || stored === '' ? '' : String(stored)
  }

  const text = String(value).trim()

  return text
}

function rowsByKey (records, column, timeZone) {

  const map = new Map()

  for (const record of records || []) {

    const raw = record && Object.prototype.hasOwnProperty.call(record, 'keyValue')
      ? record.keyValue
      : record && record[column.uniqueTitle]
    const key = keyIdentity(raw, column, timeZone)

    if (!key) continue

    const list = map.get(key) || []

    list.push(Number(record.id))
    map.set(key, list)
  }

  return map
}

function matchExistingKey (value, column, timeZone, index) {

  const key = keyIdentity(value, column, timeZone)

  if (!key) return { action: 'insert' }

  const ids = (index && index.get(key)) || []

  if (ids.length > 1) return { action: 'failed', message: 'More than one record matches this key.' }

  if (ids.length === 1) return { action: 'update', id: ids[0] }

  return { action: 'insert' }
}

function relationshipCellLabels (text, multiple) {

  const raw = String(text == null ? '' : text).trim()

  if (!raw) return []

  if (!multiple) return [raw]

  return raw.split(',').map((part) => part.trim()).filter(Boolean)
}

function matchRelationshipLabels (labels, catalog) {

  const ids = []

  for (const label of labels || []) {

    const exact = (catalog || []).filter((item) => item.label === label)

    if (exact.length === 1) {

      ids.push(Number(exact[0].id))
      continue
    }

    if (exact.length > 1) return { error: `More than one record is named "${label}".` }

    const folded = (catalog || []).filter((item) => String(item.label).toLowerCase() === String(label).toLowerCase())

    if (folded.length === 1) {

      ids.push(Number(folded[0].id))
      continue
    }

    if (folded.length > 1) return { error: `More than one record is named "${label}".` }

    return { error: `No record matches "${label}".` }
  }

  return { ids: [...new Set(ids)] }
}

function relationshipMultiple (column) {

  return relationshipChoice(column && column.relationshipType) === 'multiple'
}

function failureReportCsv (headers, rows, failures) {

  const head = ['row', 'message'].concat(headers || [])
  let csv = `\uFEFF${csvLine(head)}`

  for (const failure of failures || []) {

    const source = (rows && rows[failure.index]) || []
    const cells = (headers || []).map((_, index) => (source[index] == null ? '' : source[index]))

    csv += csvLine([failure.index + 1, failure.message || ''].concat(cells))
  }

  return csv
}

module.exports = {
  importableColumn,
  importableColumns,
  keyColumnAllowed,
  parseCsv,
  suggestColumnMap,
  normalizeColumnMap,
  keyIdentity,
  rowsByKey,
  matchExistingKey,
  relationshipCellLabels,
  matchRelationshipLabels,
  relationshipMultiple,
  failureReportCsv
}
